import { z } from 'zod';
import { brandIdField } from './brand';

/**
 * Link da empresa (A062, fase 2): o endereço que o RH divulga para as
 * colaboradoras se cadastrarem já vinculadas à empresa.
 *
 * Mesmo desenho do link de profissional (`brand-invite-link.schema.ts`):
 * hash como chave de busca, token em claro para a tela da marca mostrar
 * link e QR a qualquer momento, um ativo por empresa, gerar de novo revoga
 * o anterior. O que muda é o que protege o link de vazar:
 *
 * - **`VALIDADO`** (padrão): quem chega confirma, por código, um e-mail de um
 *   dos domínios da empresa. O vazamento não importa: de fora, ninguém tem
 *   o e-mail.
 * - **`ABERTO`**: por exceção, liberado só pelo admin da marca. Sem prova,
 *   o que contém o vazamento são as `vagas`: o número que a marca vendeu.
 *   Só cadastro concluído ocupa vaga; esgotadas, o link encerra
 *   (`ESGOTADO`) e a marca gera outro, com vagas próprias.
 *
 * O token é também o **código digitável** (`ABCD-EFGH`): quem recebe por
 * mensagem digita no cadastro em vez de clicar. Por isso é curto e sem
 * caracteres ambíguos (0/O, 1/I/L). São 31^8 combinações; a rota pública tem
 * limite por IP, e mesmo acertar um código só leva ao link validado (que
 * pede o e-mail da empresa) ou a uma vaga contada.
 */

export const ModoDoLinkDaEmpresaEnum = z.enum(['VALIDADO', 'ABERTO']);
export type ModoDoLinkDaEmpresa = z.infer<typeof ModoDoLinkDaEmpresaEnum>;

export const StatusDoLinkDaEmpresaEnum = z.enum(['ACTIVE', 'REVOKED', 'ESGOTADO']);
export type StatusDoLinkDaEmpresa = z.infer<typeof StatusDoLinkDaEmpresaEnum>;

export const CompanyInviteLinkSchema = z.object({
  id: z.string(),
  /**
   * A empresa que o link carimba. `null` = link da **marca inteira** (A062,
   * fase D): a marca corporativa, em que a própria empresa é a marca, valida
   * pelos domínios dela (`partners.dominios`) e não carimba empresa nenhuma.
   */
  companyId: z.string().nullable().default(null),
  brandId: brandIdField(),
  /** SHA-256 do código normalizado — a chave pela qual a rota pública acha o link. */
  tokenHash: z.string(),
  /** O código em claro (`ABCDEFGH`), para a tela da marca mostrar link, código e QR. */
  token: z.string(),
  modo: ModoDoLinkDaEmpresaEnum,
  /** Obrigatório no modo aberto; `null` no validado. */
  vagas: z.number().int().positive().nullable().default(null),
  status: StatusDoLinkDaEmpresaEnum.default('ACTIVE'),
  /** Quantas vezes a página do link foi aberta — o número que diz "vazou?". */
  acessos: z.number().int().nonnegative().default(0),
  /** Cadastros concluídos pelo link. No modo aberto, é o que consome vagas. */
  cadastros: z.number().int().nonnegative().default(0),
  createdByAccountId: z.string(),
  revokedAt: z.coerce.date().nullable().default(null),
  esgotadoEm: z.coerce.date().nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CompanyInviteLink = z.infer<typeof CompanyInviteLinkSchema>;

/** Sem 0/O, 1/I/L: o código é ditado e digitado. */
export const ALFABETO_DO_CODIGO_DA_EMPRESA = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const TAMANHO_DO_CODIGO_DA_EMPRESA = 8;

/** `abcd-efgh ` → `ABCDEFGH`. O que entra na busca e no hash. */
export function normalizarCodigoDaEmpresa(codigo: string): string {
  return codigo.toUpperCase().replace(/[^0-9A-Z]/g, '');
}

/** `ABCDEFGH` → `ABCD-EFGH`, para mostrar e ditar. */
export function formatarCodigoDaEmpresa(codigo: string): string {
  const c = normalizarCodigoDaEmpresa(codigo);
  return c.length === 8 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
}

/** Problemas das opções de geração. Vazio = pode gerar. */
export function validarOpcoesDoLinkDaEmpresa(
  opcoes: { modo: ModoDoLinkDaEmpresa; vagas?: number | null },
  /** A empresa, ou a marca quando o link é da marca inteira: o que importa são os domínios. */
  empresa: { dominios?: readonly string[] }
): string[] {
  const problemas: string[] = [];
  if (opcoes.modo === 'ABERTO') {
    if (!opcoes.vagas || !Number.isInteger(opcoes.vagas) || opcoes.vagas < 1) {
      problemas.push('O link aberto precisa de um número de vagas.');
    }
  } else if (!empresa.dominios?.length) {
    problemas.push('O link validado precisa de ao menos um domínio de e-mail da empresa.');
  }
  return problemas;
}

/** O link ainda aceita cadastro? */
export function linkDaEmpresaAceita(link: Pick<CompanyInviteLink, 'status' | 'modo' | 'vagas' | 'cadastros'>): boolean {
  if (link.status !== 'ACTIVE') return false;
  if (link.modo === 'ABERTO') return link.vagas !== null && link.cadastros < link.vagas;
  return true;
}
